import { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ApolloCache, ApolloError, useQuery } from '@apollo/client';

import { useFeaturedFlagEnabled } from 'helpers/posthogHooks';
import { ANALYTICS_EVENTS, EFeaturedFlags } from 'constants/analyticsEvents';
import { EMPTY_ITEMS, EQueryFetchPolicy, SHOW_FETCH_MORE_LOADER } from 'constants/common';
import {
  CardSchema,
  GetMessageContentDocument,
  GetMessageContentQuery,
  GetMessageContentQueryVariables,
  GetSparkDetailCardsDocument,
  GetSparkDetailCardsQuery,
  GetUserSparksDocument,
  MessageContentStatus,
  MessageItemType,
  useAddCardToSparkMutation,
  useCreateSparkMutation,
  useDeleteSparkMutation,
  useGetFeaturedSparkCardsQuery,
  useGetSparkCardsQuery,
  useGetSparkDetailCardsQuery,
  useGetSparkDetailQuery,
  useGetSparksGradientsQuery,
  useGetUserSparksQuery,
  useUpdateSparkMutation,
} from 'constants/graphqlTypes';
import { useGetUserId } from 'graphQL/profile/hooks';
import {
  IUseAddResponseToSparkArgs,
  IUseGetSparkDetail,
  IUseGetUserSparks,
  IUseCreateSparkArgs,
  IUseGetSparksGradients,
  IUseUpdateSparkArgs,
} from 'graphQL/sparks/models';
import { updateUserCards } from 'graphQL/cards/userCards/helpers';
import useToast from 'helpers/useToast';
import { ROUTE_ADD_CONTENT } from 'routes';
import getAuthUser from 'helpers/getAuthUser';

import { useAnalytics } from 'contexts/AnalyticsContext';
import {
  updateAllSparksCardsQueryCache,
  updateSparkCardsQueryCache,
  addCardToExistingCards,
  updateFeaturedSparksCardsQueryCache,
} from './helpers';

// Import the query
import { GET_SPARK_CATEGORIES } from './gql';

export const useGetSparksGradients = (): IUseGetSparksGradients => {
  const { data, loading } = useGetSparksGradientsQuery();

  const { items, totalCount } = data?.allColors ?? EMPTY_ITEMS;

  return {
    items,
    totalCount,
    loading,
  };
};

export const useCreateSpark = ({
  title,
  bgColorId,
  isPrivate = true,
  coverMediaId,
  categorySlug,
  description,
  onCompleted,
}: IUseCreateSparkArgs): { handleDone: () => void; loading: boolean } => {
  const { setToast } = useToast();
  const { track } = useAnalytics();
  const isInteractSparks = useFeaturedFlagEnabled(EFeaturedFlags.InteractSpark);

  const { push } = useHistory();
  const { state } = useLocation<{ variable?: { [key: string]: string | undefined } }>();

  const [createSpark, { data, loading }] = useCreateSparkMutation({
    onError: (error: ApolloError) => {
      setToast({
        isToastOpen: true,
        toastItemName: error.message,
        toastError: true,
      });
    },
    onCompleted: () => {
      setToast({
        customFontWeight: 400,
        isToastOpen: true,
        toastItemName: 'Your spark is live. Why not get it started with a first pick?',
        linesCount: 2,
        toastLinkText: isInteractSparks ? 'Add' : undefined,
        handleToastButton: () =>
          push({
            pathname: ROUTE_ADD_CONTENT,
            state: { ...state, variable: { spark: data?.createSpark } },
          }),
      });
      track(ANALYTICS_EVENTS.CreateSpark);
      onCompleted?.();
    },
    refetchQueries: [{ query: GetUserSparksDocument, variables: { id: getAuthUser().userId, limit: undefined } }],
  });

  // Pass variables at call time to use current values
  const handleDone = () => {
    createSpark({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      variables: { title, bgColorId, isPrivate, coverMediaId, categorySlug, description } as any,
    });
  };

  return { handleDone, loading };
};

export const useUpdateSpark = ({
  sparkId,
  title,
  bgColorId,
  onCompleted,
}: IUseUpdateSparkArgs): { handleUpdate: () => void; loading: boolean } => {
  const { setToast } = useToast();

  const [updateSpark, { loading }] = useUpdateSparkMutation({
    variables: { sparkId, title, bgColorId },
    onError: (error) => {
      setToast({
        isToastOpen: true,
        toastItemName: error.message,
        toastError: true,
      });
    },
    onCompleted: () => {
      setToast({
        isToastOpen: true,
        toastItemName: 'Spark updated successfully',
      });
      onCompleted?.();
    },
    refetchQueries: [{ query: GetUserSparksDocument, variables: { id: getAuthUser().userId, limit: undefined } }],
  });

  return { handleUpdate: () => updateSpark(), loading };
};

export const useGetUserSparks = (params?: { limit?: number; userId: string }): IUseGetUserSparks => {
  const { limit, userId } = params ?? {};
  const { profileName } = useParams<{ profileName: string }>();
  const { userId: id } = useGetUserId(profileName);
  const { data, fetchMore, loading } = useGetUserSparksQuery({
    variables: {
      id: userId ?? id ?? '',
      limit,
    },
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    ...SHOW_FETCH_MORE_LOADER,
    skip: !userId && !id,
  });

  const { items, totalCount } = data?.allUsers?.items[0]?.pickedCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) => updateUserCards(prev, fetchMoreResult),
    });

  return {
    // Cast to CardSchema[] - the query returns a subset of fields but that's ok
    items: items as CardSchema[],
    loadMore,
    totalCount,
    loading,
  };
};

export const useGetSparkDetail = (id?: string): IUseGetSparkDetail => {
  const { sparkId } = useParams<{ sparkId: string }>();
  const [isQueryCalled, setIsQueryCalled] = useState(false);
  const { data, error } = useGetSparkDetailQuery({
    variables: {
      sparkId: id ?? sparkId,
    },
    skip: !sparkId && !id,
    onCompleted: () => setIsQueryCalled(true),
    onError: () => setIsQueryCalled(true),
  });

  // Cast to CardSchema - the query returns a subset of fields but that's ok
  return { spark: data?.allCards?.items?.[0] as CardSchema | undefined, isQueryCalled, error };
};

export const useGetSparkDetailCards = (spark?: CardSchema) => {
  const { data, loading, fetchMore } = useGetSparkDetailCardsQuery({
    variables: { sparkId: spark?.id, sparkCardId: spark?.cardId, offset: 0, limit: 12 },
    skip: !spark,
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
  });

  const { items, totalCount } = data?.allCards?.items?.[0].sparkResponses ?? {};

  const loadMore = () =>
    fetchMore({
      variables: {
        offset: items?.length,
        limit: 12,
      },
      updateQuery: (prev, { fetchMoreResult }) => updateSparkCardsQueryCache(prev, fetchMoreResult),
    });

  return {
    items,
    totalCount: totalCount || 0,
    loadMore,
    loading,
  };
};

export const useDeleteSpark = () => {
  const [deleteMutation] = useDeleteSparkMutation();

  return (sparkCardId: string, onCompleted?: () => void) =>
    deleteMutation({
      variables: { sparkId: sparkCardId },
      onCompleted,
      refetchQueries: [{ query: GetUserSparksDocument, variables: { id: getAuthUser().userId, limit: undefined } }],
      update: (cache: ApolloCache<GetMessageContentQuery>, { data }) => {
        if (!data) {
          return;
        }

        cache.updateQuery<GetMessageContentQuery, GetMessageContentQueryVariables>(
          {
            query: GetMessageContentDocument,
            variables: {
              sparkId: sparkCardId,
              itemType: MessageItemType.Spark,
            },
          },
          (oldMessageContent) => ({
            ...oldMessageContent,
            getMessageContent: {
              ...oldMessageContent?.getMessageContent,
              messageContentStatus: MessageContentStatus.Deleted,
            },
          })
        );
      },
    });
};

export const useAddResponseToSpark = ({ spark, onCompleted }: IUseAddResponseToSparkArgs) => {
  const { track } = useAnalytics();

  const [addAnswerMutation, { loading, data: mutationData }] = useAddCardToSparkMutation({
    update: (cache: ApolloCache<GetSparkDetailCardsQuery>, { data }) => {
      cache.updateQuery(
        {
          query: GetSparkDetailCardsDocument,
          variables: { sparkId: spark.id, sparkCardId: spark.cardId, offset: 0, limit: 12 },
        },
        (query: GetSparkDetailCardsQuery | null): GetSparkDetailCardsQuery => {
          if (
            !(
              query?.allCards?.items?.[0]?.sparkResponses?.items &&
              data?.addCardToSpark?.sparkResponses?.totalCount &&
              data.addCardToSpark.sparkResponses.items?.[0]
            )
          ) {
            return { ...query };
          }

          const { cards: newResponses, totalCount } = addCardToExistingCards(
            query.allCards.items[0].sparkResponses.items,
            data.addCardToSpark.sparkResponses.items?.[0],
            query.allCards.items[0].sparkResponses.totalCount
          );
          return {
            ...query,
            allCards: {
              ...query?.allCards,
              items: [
                {
                  ...query.allCards.items[0],
                  sparkResponses: {
                    ...query.allCards.items[0].sparkResponses,
                    totalCount,
                    items: newResponses,
                  },
                },
              ],
            },
          };
        }
      );
    },
    refetchQueries: [
      {
        query: GetUserSparksDocument,
        variables: {
          id: getAuthUser().userId,
          limit: undefined,
        },
      },
    ],
    onCompleted: () => {
      track(ANALYTICS_EVENTS.AnswerQuestion);
      track(ANALYTICS_EVENTS.SparkAction);
    },
  });

  const addResponse = (pickId: string) =>
    addAnswerMutation({
      variables: {
        pickId,
        sparkId: spark.cardId,
      },
      onCompleted,
    });

  return { loading, data: mutationData, addResponse };
};

export const useGetSparkCards = (params?: { limit?: number }): IUseGetUserSparks => {
  const { limit } = params ?? {};

  const { data, fetchMore, loading } = useGetSparkCardsQuery({
    variables: {
      limit,
    },
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    ...SHOW_FETCH_MORE_LOADER,
  });

  const { items, totalCount } = data?.allCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) => updateAllSparksCardsQueryCache(prev, fetchMoreResult),
    });

  return {
    items: items as CardSchema[],
    loadMore,
    totalCount,
    loading,
  };
};

export const useGetFeaturedSparkCards = (params?: { limit?: number }): IUseGetUserSparks => {
  const { limit } = params ?? {};

  const { data, fetchMore, loading } = useGetFeaturedSparkCardsQuery({
    variables: {
      limit,
    },
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
    ...SHOW_FETCH_MORE_LOADER,
  });

  const { items, totalCount } = data?.allCards ?? EMPTY_ITEMS;

  const loadMore = () =>
    items.length < totalCount &&
    fetchMore({
      variables: { offset: items.length },
      updateQuery: (prev, { fetchMoreResult }) => updateFeaturedSparksCardsQueryCache(prev, fetchMoreResult),
    });

  return {
    items: items as CardSchema[],
    loadMore,
    totalCount,
    loading,
  };
};

// Types for spark categories
export interface ISparkCoverMedia {
  id: string;
  name: string | null;
  mediaUrl: string | null;
  mediaType: 'IMAGE' | 'ANIMATION';
  position: number;
  isActive: boolean;
  thumbnailUrl: string | null;
  gradientStart: string | null;
  gradientEnd: string | null;
}

export interface ISparkCategory {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  position: number;
  isActive: boolean;
  coverMedia: ISparkCoverMedia[];
}

interface ISparkCategoriesResult {
  allSparkCategories: {
    totalCount: number;
    items: ISparkCategory[];
  };
}

export interface IUseGetSparkCategories {
  items: ISparkCategory[];
  totalCount: number;
  loading: boolean;
}

export const useGetSparkCategories = (params?: { limit?: number }): IUseGetSparkCategories => {
  const { limit } = params ?? {};

  const { data, loading } = useQuery<ISparkCategoriesResult>(GET_SPARK_CATEGORIES, {
    variables: {
      limit,
    },
    fetchPolicy: EQueryFetchPolicy.CacheAndNetwork,
  });

  const { items, totalCount } = data?.allSparkCategories ?? { items: [], totalCount: 0 };

  return {
    items,
    totalCount,
    loading,
  };
};
